import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { campusApi } from "./campus-api";
import { Node, Campus, CreateNodeDto } from "./types";
import NodeFormModal from "./components/NodeFormModal";
import TokenModal from "./components/TokenModal";

export default function NodeManagement() {
  const { campusId } = useParams<{ campusId: string }>();
  const navigate = useNavigate();
  const [campus, setCampus] = useState<Campus | null>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<Node | null>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  useEffect(() => {
    if (campusId) {
      fetchCampus();
      fetchNodes();
    }
  }, [campusId]);

  const fetchCampus = async () => {
    if (!campusId) return;
    try {
      const data = await campusApi.getCampusById(Number(campusId));
      setCampus(data);
    } catch (error: any) {
      toast.error("Failed to load campus");
    }
  };

  const fetchNodes = async () => {
    if (!campusId) return;
    setLoading(true);
    try {
      const data = await campusApi.getNodesByCampus(Number(campusId));
      setNodes(data);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to load nodes");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingNode(null);
    setModalOpen(true);
  };

  const handleEdit = (node: Node) => {
    setEditingNode(node);
    setModalOpen(true);
  };

  const handleDelete = async (nodeId: string) => {
    if (!window.confirm("Are you sure you want to delete this node? All associated cameras will also be deleted.")) {
      return;
    }

    try {
      await campusApi.deleteNode(nodeId);
      toast.success("Node deleted successfully");
      fetchNodes();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to delete node");
    }
  };

  const handleGenerateToken = (node: Node) => {
    setSelectedNode(node);
    setTokenModalOpen(true);
  };

  const handleViewNode = (node: Node) => {
    navigate(`/campuses/${campusId}/nodes/${node.id}`);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingNode(null);
    fetchNodes();
  };

  const handleTokenModalClose = () => {
    setTokenModalOpen(false);
    setSelectedNode(null);
    fetchNodes();
  };

  if (!campusId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Invalid campus ID
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => navigate("/campuses")}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4 flex items-center gap-2"
              >
                <i className="fa-solid fa-arrow-left"></i>
                <span>Back to Campuses</span>
              </button>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Nodes - {campus?.name || "Loading..."}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage nodes for {campus?.location || "this campus"}
              </p>
            </div>
            <button
              onClick={handleCreate}
              className="btn-primary px-4 py-2 flex items-center space-x-2"
            >
              <i className="fa-solid fa-plus"></i>
              <span>Add Node</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : nodes.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No nodes found. Click "Add Node" to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Node ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Token Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Token Expiry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Login Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {nodes.map((node) => (
                    <tr
                      key={node.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                        {String(node.node_id).substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {node.node_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {node.access_token ? (
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium">
                            No Token
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {node.tokenExpiry ? new Date(node.tokenExpiry).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex flex-col gap-1">
                          {node.isLoggedIn && node.isActive ? (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                              Active
                            </span>
                          ) : node.isLoggedIn ? (
                            <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                              Logged In
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                              Offline
                            </span>
                          )}
                          {node.lastSeen && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Last seen: {new Date(node.lastSeen).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewNode(node)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                            title="View node"
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          <button
                            onClick={() => handleGenerateToken(node)}
                            className={node.access_token 
                              ? "text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 transition-colors"
                              : "text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors"
                            }
                            title={node.access_token ? "Regenerate token" : "Generate token"}
                          >
                            <i className="fa-solid fa-key"></i>
                          </button>
                          <button
                            onClick={() => handleEdit(node)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                            title="Edit node"
                          >
                            <i className="fa-solid fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(node.node_id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                            title="Delete node"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <NodeFormModal
        open={modalOpen}
        onClose={handleModalClose}
        node={editingNode}
        campusId={Number(campusId)}
      />

      <TokenModal
        open={tokenModalOpen}
        onClose={handleTokenModalClose}
        node={selectedNode}
      />
    </div>
  );
}

